/**
 * InlineEditableField - Campo editável inline com estado local
 * Salva automaticamente ao sair do campo (blur) para evitar múltiplas requisições
 * 
 * Versão: 1.0.0
 * - Permite digitação fluida sem disparar updates a cada tecla
 * - Sincroniza com valor externo quando não está em foco
 */
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Props do componente
interface InlineEditableFieldProps {
  /** Valor atual do campo */
  value: number;
  /** Callback chamado ao sair do campo (blur) com o novo valor */
  onChange: (value: number) => void;
  /** Valor mínimo permitido (default: 0) */
  min?: number;
  /** Valor máximo permitido (opcional) */
  max?: number;
  /** Placeholder quando vazio */
  placeholder?: string;
  /** Classes CSS adicionais */
  className?: string;
  /** Se true, permite valores decimais */
  allowDecimals?: boolean;
}

/**
 * Campo de edição inline com estado local
 * Evita múltiplas requisições ao backend durante digitação
 */
export function InlineEditableField({
  value,
  onChange,
  min = 0,
  max,
  placeholder = '-',
  className,
  allowDecimals = true,
}: InlineEditableFieldProps) {
  // Estado local para edição fluida
  const [localValue, setLocalValue] = useState<string>(value ? String(value) : '');
  const [isFocused, setIsFocused] = useState(false);

  // Sincroniza com prop externa quando não está em foco
  useEffect(() => {
    if (!isFocused) {
      setLocalValue(value ? String(value) : '');
    }
  }, [value, isFocused]);

  /**
   * Handler de foco - marca campo como ativo
   */
  const handleFocus = () => {
    setIsFocused(true);
  };

  /**
   * Handler de blur - salva valor ao sair do campo
   */
  const handleBlur = () => {
    setIsFocused(false);
    
    // Parse do valor local - normaliza vírgula para ponto (padrão BR)
    const normalized = localValue.replace(',', '.');
    const numValue = allowDecimals 
      ? parseFloat(normalized) || 0 
      : parseInt(normalized) || 0;
    
    // Aplica limites min/max
    let clampedValue = Math.max(min, numValue);
    if (max !== undefined) {
      clampedValue = Math.min(max, clampedValue);
    }
    
    // Atualiza estado local com valor corrigido
    setLocalValue(clampedValue ? String(clampedValue) : '');
    
    // Só chama onChange se valor realmente mudou
    if (clampedValue !== value) {
      onChange(clampedValue);
    }
  };

  /**
   * Handler de mudança - atualiza apenas estado local
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    
    // Regex para permitir números e opcionalmente ponto ou vírgula decimal (padrão BR)
    const regex = allowDecimals ? /^[0-9]*[.,]?[0-9]*$/ : /^[0-9]*$/;
    
    // Só atualiza se input for válido
    if (raw === '' || regex.test(raw)) {
      setLocalValue(raw);
    }
  };

  /**
   * Handler de tecla Enter - salva e remove foco
   */
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <Input
      type="text"
      inputMode="decimal"
      value={localValue}
      placeholder={placeholder}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className={cn(className)}
    />
  );
}
